import { supabase } from '../integrations/supabase/client';

/**
 * Função para limpar imagens duplicadas de condomínios nos imóveis
 * Remove imagens que foram erroneamente salvas no campo images do imóvel
 * mas que na verdade são do condomínio
 */
export const cleanDuplicateCondominiumImages = async (propertyId: string) => {
  try {
    console.log('🧹 Iniciando limpeza de imagens duplicadas para:', propertyId);
    
    // Buscar o imóvel e suas imagens
    const { data: property, error: propertyError } = await supabase
      .from('properties')
      .select('id, listing_id, images, condominium_id')
      .eq('listing_id', propertyId)
      .single();
    
    if (propertyError || !property) {
      console.error('❌ Erro ao buscar imóvel:', propertyError);
      return { success: false, error: 'Imóvel não encontrado' };
    }
    
    console.log('📊 Imóvel encontrado:', {
      id: property.id,
      listing_id: property.listing_id,
      condominium_id: property.condominium_id,
      total_images: Array.isArray(property.images) ? property.images.length : 0
    });
    
    // Se não tem condomínio, não precisa limpar
    if (!property.condominium_id) {
      console.log('ℹ️ Imóvel não tem condomínio associado');
      return { success: true, message: 'Imóvel não possui condomínio' };
    }
    
    // Buscar as imagens do condomínio
    const { data: condominium, error: condominiumError } = await supabase
      .from('condominiums')
      .select('images')
      .eq('id', property.condominium_id)
      .single();
    
    if (condominiumError || !condominium) {
      console.error('❌ Erro ao buscar condomínio:', condominiumError);
      return { success: false, error: 'Condomínio não encontrado' };
    }
    
    const condominiumImages = Array.isArray(condominium.images) ? condominium.images : [];
    console.log('🏢 Imagens do condomínio:', condominiumImages.length);
    
    // Filtrar imagens do imóvel, removendo apenas as que são EXATAMENTE IGUAIS às do condomínio
    const propertyImages = Array.isArray(property.images) ? property.images : [];
    const condominiumUrls = new Set(condominiumImages.map((img: any) => img.url));
    
    const cleanedImages = propertyImages.filter((img: any) => {
      const isDuplicate = condominiumUrls.has(img.url);
      if (isDuplicate) {
        console.log('🗑️ Removendo imagem duplicada do condomínio:', img.url);
      }
      return !isDuplicate;
    });
    
    console.log('📊 Resultado da limpeza:', {
      images_antes: propertyImages.length,
      images_depois: cleanedImages.length,
      duplicadas_removidas: propertyImages.length - cleanedImages.length
    });
    
    // Atualizar o imóvel com as imagens limpas
    const { error: updateError } = await supabase
      .from('properties')
      .update({ 
        images: cleanedImages,
        updated_at: new Date().toISOString()
      })
      .eq('id', property.id);
    
    if (updateError) {
      console.error('❌ Erro ao atualizar imóvel:', updateError);
      return { success: false, error: 'Erro ao atualizar imóvel' };
    }
    
    console.log('✅ Limpeza concluída com sucesso!');
    return { 
      success: true, 
      message: `${propertyImages.length - cleanedImages.length} imagens duplicadas removidas`,
      images_removed: propertyImages.length - cleanedImages.length,
      images_remaining: cleanedImages.length
    };
    
  } catch (error) {
    console.error('💥 Erro na limpeza:', error);
    return { success: false, error: 'Erro interno na limpeza' };
  }
};

/**
 * Função para limpar todas as propriedades com imagens duplicadas
 */
export const cleanAllDuplicateImages = async () => {
  try {
    console.log('🧹 Iniciando limpeza geral de imagens duplicadas...');
    
    // Buscar todos os imóveis que têm condomínio
    const { data: properties, error } = await supabase
      .from('properties')
      .select('listing_id, condominium_id')
      .not('condominium_id', 'is', null)
      .eq('is_active', true);
    
    if (error) {
      console.error('❌ Erro ao buscar imóveis:', error);
      return { success: false, error: 'Erro ao buscar imóveis' };
    }
    
    console.log(`📊 Encontrados ${properties.length} imóveis com condomínio`);
    
    let totalCleaned = 0;
    const results = [];
    
    for (const property of properties) {
      const result = await cleanDuplicateCondominiumImages(property.listing_id);
      if (result.success && result.images_removed && result.images_removed > 0) {
        totalCleaned += result.images_removed;
        results.push({
          listing_id: property.listing_id,
          images_removed: result.images_removed
        });
      }
    }
    
    console.log(`✅ Limpeza geral concluída! ${totalCleaned} imagens duplicadas removidas de ${results.length} imóveis`);
    
    return {
      success: true,
      message: `${totalCleaned} imagens duplicadas removidas de ${results.length} imóveis`,
      total_images_removed: totalCleaned,
      properties_cleaned: results.length,
      details: results
    };
    
  } catch (error) {
    console.error('💥 Erro na limpeza geral:', error);
    return { success: false, error: 'Erro interno na limpeza geral' };
  }
};